import * as THREE from 'three';
import { cairnsLayout, echoLayout, fallenLayout, hexToWorld, type BattleState, type CairnLayout, type EchoLayout, type FallenLayout, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX, facingToRotY } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';
import { GALO_PLACES, GaloLayer } from './galo.ts';

/**
 * Aster's stones (docs/sendas/explorador.md, chapter 1), for the ranger's path: live stone and moss over the deep ways
 * of the Furrows, the sea to the east, an overcast sky with the sun low in the west; then the fog inside a Furrow; then
 * a slope of the shell's loose plates where the titan moved. Its puzzles are sound and distance: the rings drawn on the
 * ground a hundred paces apart, the shout's ring and the echoes coming back red off the walls.
 *
 * - `furrowRim` (act 1, the knots): the rim, two Furrows cut across it (dark grooves with mossy lips), the cliff's edge
 *   on the east and the sea far below (its foam bright), long shadows to the east from everything (the sun is west).
 *   The cairns, each its own stack of stones with a cord and its knots hanging; some on a high plate. A cairn read: its
 *   rings on the ground (hexagons a hundred paces apart, numbered), the line between the sun's half and the sea's, the
 *   sun and the sea marked at its two sides. A cairn found: the red thread to it from the last; a false one comes apart.
 * - `furrowFog` (act 2): inside a Furrow, its walls of live stone, wet floor, two banks of fog that open only round the
 *   hero; Sela's shout a pale ring going out, the dead ends' walls flashing red as it reaches them, and the echo coming
 *   back red from each to her.
 * - `looseSlope` (act 3, the test): the shell's plates lying loose and tilted, slabs standing up; the cairns standing,
 *   the light bases of those that fell (cracked when wrong); the rings of the last two cairns read, the long cord's red
 *   and the other's pale; the Roller drawn here (`owns`): an armadillo of plates that curls into a ball (its lane red on
 *   the ground while it curls) and rolls, dust behind it, stars round it stunned against a cairn.
 * - The scenes: `asterMap` (Aster's map at night, on Orla's table), `asterTable` (the Atalaya with the map pinned to its
 *   wall), `asterYard` (the yard, the map on two barrels), `furrowLip` (a cairn at the lip of a Furrow, its fresh notch),
 *   `furrowCamp` (under a standing plate at night), `fallenBase` (the base with the red cord, at dusk).
 *
 * Draw calls: the dressing (one merged mesh), each cairn (one each, eight at most, so a false one can come apart), the
 * rings and the thread (one mesh each, rebuilt when they change), the fog (two planes), the shout and echo rings (an
 * instanced pool), the bases (one mesh), the Roller (a handful), the glow layer, the sky, the sea. No lights of its own.
 */

export const ASTER_PLACES = new Set(['furrowRim', 'furrowFog', 'looseSlope', 'asterMap', 'asterTable', 'asterYard', 'furrowLip', 'furrowCamp', 'fallenBase', ...GALO_PLACES]);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const PALE = new THREE.Color('#e4ebf0');
/** The soot crows' eyes in the fog (and their faint halo). */
const EMBER_EYE = new THREE.Color('#ff7a4a');
const EMBER_HALO = new THREE.Color('#ff4f3a');
const SEA = new THREE.Color('#9fd2e6');
const COLD = new THREE.Color('#dce8ff');
const WARM = new THREE.Color('#ffb48a');
const STONE = ['#66706b', '#5b6560', '#727c76', '#505a55', '#5f6a64'];
const MOSS = ['#4f6d4b', '#5d7d54', '#425f43', '#6a8a5c'];
const SLATE = ['#5d6268', '#4f545a', '#6c7278', '#454a50'];
const CORD = '#9a3a30';
const CORD_NEW = '#d8382c';
const DARK = '#1b2321';

type Hour = 'day' | 'dusk' | 'night' | 'dawn' | 'fog' | 'inside';

/** The light by place and hour: an overcast day on the rim, white fog, the slope's late light, night, a room. */
const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#a3adb4', fog: ['#a7b1b8', 34, 130], hemi: ['#dfe6ea', '#2c322f', 2.1], key: ['#fff4ec', 2.4], rim: ['#b4c6dc', 1.2], sky: ['#6f7f8c', '#c6ced3', '#fff4ea'] },
  dusk: { bg: '#4a4f5e', fog: ['#565a6a', 28, 120], hemi: ['#c9d2e2', '#1d2026', 2.3], key: ['#ffd8c8', 2.8], rim: ['#9ab4f0', 1.8], sky: ['#1d2438', '#6f7690', '#ff9474'] },
  dawn: { bg: '#a9b5c4', fog: ['#aab6c4', 30, 120], hemi: ['#d0d9e6', '#25272e', 2.0], key: ['#ffece2', 2.6], rim: ['#a9bfee', 1.5], sky: ['#5a6d88', '#d7dee8', '#fff1ea'] },
  night: { bg: '#080b11', fog: ['#0c1019', 22, 90], hemi: ['#7487b0', '#0b0c10', 1.2], key: ['#a9bbe6', 1.0], rim: ['#7a96ff', 1.2], sky: ['#020409', '#0f1520', '#9fb2e2'] },
  // (The fog's far: the banks over the Furrow are the fog; the scene's starts far off, or it washes out what's near on a phone.)
  fog: { bg: '#5d686f', fog: ['#6a767d', 34, 90], hemi: ['#dfe7ec', '#262c30', 1.9], key: ['#f4f7fa', 2.3], rim: ['#c8d6e2', 1.2], sky: ['#55616a', '#7c878e', '#b8c2c8'] },
  inside: { bg: '#07090c', fog: ['#090b0f', 18, 52], hemi: ['#a3afc6', '#16130f', 1.9], key: ['#d6deee', 1.4], rim: ['#ff7a5a', 0.6], sky: ['#000000', '#000000', '#000000'] },
};

/** The sea far below the rim: a slow swell, foam in lines, white where it breaks on the cliff (west). */
const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, edge: { value: 12 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float edge; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float sw = n(vXZ * 0.16 + vec2(time * 0.05, time * 0.02)) * 0.6 + n(vXZ * 0.42 - vec2(time * 0.08, 0.0)) * 0.4;
        float foam = smoothstep(0.72, 0.8, n(vec2(vXZ.x * 0.5 - time * 0.12, vXZ.y * 1.6 + sw * 3.0))) * smoothstep(0.35, 0.6, sw);
        float shore = smoothstep(9.0, 0.0, vXZ.x - edge);
        vec3 deep = vec3(0.10, 0.17, 0.22);
        vec3 col = mix(deep, vec3(0.2, 0.3, 0.36), sw * 0.6);
        col = mix(col, vec3(0.86, 0.92, 0.96), max(foam * 0.5, shore * smoothstep(0.4, 0.7, sw) * 0.85));
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/**
 * A bank of fog over the Furrow: slate grey, drifting, open in a round about the hero and another about Sela (`center`,
 * `center2`; `r0` clear to `r1` thick). Lit like a cloud's top: the noise is read twice, a step toward the light, so
 * its ridges catch the light and its hollows go dark (depth, not a flat wash). `lo`/`hi`: the hollows' and the ridges'
 * colour; `scale`: the bank's noise size; `fade`: how far below full the bank goes at its thickest. One plane per
 * bank: the board's fog is uniforms, never a particle per puff (docs/RENDIMIENTO.md).
 */
const fogMat = (alpha: number, lo: string, hi: string, scale: number, seed: number): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      center: { value: new THREE.Vector2() },
      center2: { value: new THREE.Vector2(9999, 9999) },
      r0: { value: 2.2 },
      r1: { value: 3.4 },
      r2: { value: 1.2 },
      alpha: { value: alpha },
      lo: { value: new THREE.Color(lo) },
      hi: { value: new THREE.Color(hi) },
      scale: { value: scale },
      seed: { value: seed },
    },
    transparent: true,
    depthWrite: false,
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform vec2 center; uniform vec2 center2; uniform float r0; uniform float r1; uniform float r2;
      uniform float alpha; uniform vec3 lo; uniform vec3 hi; uniform float scale; uniform float seed; varying vec2 vXZ;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      float fbm(vec2 p) { return n(p) * 0.55 + n(p * 2.1 + 7.3) * 0.3 + n(p * 4.3 - 3.1) * 0.15; }
      void main() {
        vec2 p = vXZ * scale + vec2(seed, seed * 0.7);
        vec2 w = vec2(time * 0.05, time * 0.022);
        float d0 = fbm(p + w);
        // A step toward the light (from the west, a little north): ridges lit, hollows in shade.
        float d1 = fbm(p + w + vec2(0.22, 0.12));
        float lit = clamp(0.5 + (d0 - d1) * 3.2, 0.0, 1.0);
        float d = min(length(vXZ - center), length(vXZ - center2) + (r0 - r2));
        // (The top bank stays whole where it is thick: through it the Furrow's walls would draw the way.)
        float a = smoothstep(r0, r1 + d0 * 0.9, d) * (alpha >= 1.0 ? 1.0 : 0.7 + 0.3 * d0);
        vec3 c = mix(lo, hi, lit * 0.75 + d0 * 0.35);
        gl_FragColor = vec4(c, a * alpha);
      }`,
  });

/** The shout's ring: three bands (dark, bright, dark) in one flat geometry, radius 1, vertex-coloured. */
const soundRing = (): THREE.BufferGeometry => {
  const bands: [number, number, number][] = [
    [0.95, 0.962, 0.1],
    [0.962, 0.99, 1],
    [0.99, 1.002, 0.1],
  ];
  const pos: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const seg = 56;
  for (const [a, b, k] of bands) {
    const base = pos.length / 3;
    for (let i = 0; i <= seg; i++) {
      const t = (i / seg) * Math.PI * 2;
      const c = Math.cos(t);
      const sn = Math.sin(t);
      pos.push(c * a, 0, sn * a, c * b, 0, sn * b);
      col.push(k, k, k, k, k, k);
    }
    for (let i = 0; i < seg; i++) {
      const v = base + i * 2;
      idx.push(v, v + 2, v + 1, v + 1, v + 2, v + 3);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  return g;
};

/**
 * A soot crow seen from above in the fog: a flat silhouette (body, spread wings with their fingers, a fanned tail),
 * nose along +X, wingspan along Z, a metre and a half across. One instanced mesh draws a flock (aster's fog).
 */
const crowShape = (): THREE.BufferGeometry => {
  const sh = new THREE.Shape();
  const pts: [number, number][] = [
    [0.36, 0], [0.24, 0.06], [0.12, 0.1], [0.06, 0.3], [0.02, 0.5], [-0.02, 0.7], [-0.08, 0.66], [-0.06, 0.58], [-0.14, 0.6],
    [-0.12, 0.5], [-0.2, 0.5], [-0.16, 0.38], [-0.22, 0.34], [-0.12, 0.14], [-0.22, 0.12], [-0.42, 0.16], [-0.38, 0], [-0.42, -0.16],
    [-0.22, -0.12], [-0.12, -0.14], [-0.22, -0.34], [-0.16, -0.38], [-0.2, -0.5], [-0.12, -0.5], [-0.14, -0.6], [-0.06, -0.58],
    [-0.08, -0.66], [-0.02, -0.7], [0.02, -0.5], [0.06, -0.3], [0.12, -0.1], [0.24, -0.06],
  ];
  sh.moveTo(pts[0]![0], pts[0]![1]);
  for (const [x, y] of pts.slice(1)) sh.lineTo(x, y);
  return new THREE.ShapeGeometry(sh).rotateX(Math.PI / 2);
};

/** A digit (or a short mark) drawn on a canvas, for a sprite: the rings' numbers, the sun and the sea. */
const markTex = (text: string, color: string, glyph?: 'sun' | 'sea'): THREE.CanvasTexture => {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  g.clearRect(0, 0, 64, 64);
  g.fillStyle = 'rgba(12,14,18,0.72)';
  g.beginPath();
  g.arc(32, 32, 26, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = color;
  g.lineWidth = 3;
  g.stroke();
  if (glyph === 'sun') {
    g.fillStyle = '#f4f7fa';
    g.beginPath();
    g.arc(32, 32, 9, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = '#f4f7fa';
    g.lineWidth = 3;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      g.beginPath();
      g.moveTo(32 + Math.cos(a) * 13, 32 + Math.sin(a) * 13);
      g.lineTo(32 + Math.cos(a) * 19, 32 + Math.sin(a) * 19);
      g.stroke();
    }
  } else if (glyph === 'sea') {
    g.strokeStyle = '#bfe6f4';
    g.lineWidth = 3;
    for (const y of [24, 33, 42]) {
      g.beginPath();
      for (let x = 14; x <= 50; x += 2) g.lineTo(x, y + Math.sin((x / 36) * Math.PI * 2.5) * 3);
      g.stroke();
    }
  } else {
    g.fillStyle = '#f4f7fa';
    g.font = 'bold 34px sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(text, 32, 34);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

/**
 * Aster's map (the scenes): the Turtle's shell drawn in red ink on pale grey paper, its plates hatched, the coast and
 * the sea round it, the Furrows, and under the rim a white patch where nothing is drawn; a hand's notes in the margin.
 */
const mapTex = (): THREE.CanvasTexture => {
  const c = document.createElement('canvas');
  c.width = 768;
  c.height = 512;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  g.fillStyle = '#c9d0d6';
  g.fillRect(0, 0, 768, 512);
  // The paper's grain and stains.
  for (let i = 0; i < 900; i++) {
    const x = (Math.sin(i * 12.9898) * 43758.5453) % 1;
    const y = (Math.sin(i * 78.233) * 12345.678) % 1;
    g.fillStyle = `rgba(80,90,100,${0.03 + (i % 5) * 0.01})`;
    g.fillRect(Math.abs(x) * 768, Math.abs(y) * 512, 2, 2);
  }
  g.strokeStyle = '#8d2a22';
  g.lineWidth = 3;
  // The shell: a long oval, its plates.
  g.beginPath();
  g.ellipse(384, 230, 250, 150, 0, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 1.5;
  for (let i = 0; i < 7; i++) {
    g.beginPath();
    g.ellipse(384, 230, 250 - i * 32, 150 - i * 19, 0, Math.PI * 1.05, Math.PI * 1.95);
    g.stroke();
  }
  for (let i = -4; i <= 4; i++) {
    g.beginPath();
    g.moveTo(384 + i * 50, 100);
    g.lineTo(384 + i * 58, 360);
    g.stroke();
  }
  // Hatching on the plates' east sides.
  g.strokeStyle = 'rgba(141,42,34,0.45)';
  for (let i = 0; i < 60; i++) {
    const x = 220 + (i % 12) * 30;
    const y = 140 + Math.floor(i / 12) * 40;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + 8, y + 10);
    g.stroke();
  }
  // The head and the legs.
  g.strokeStyle = '#8d2a22';
  g.lineWidth = 2.5;
  g.beginPath();
  g.ellipse(660, 230, 40, 28, 0, 0, Math.PI * 2);
  g.stroke();
  for (const [x, y] of [
    [220, 360],
    [540, 360],
    [230, 100],
    [530, 100],
  ] as [number, number][]) {
    g.beginPath();
    g.ellipse(x, y, 34, 22, 0, 0, Math.PI * 2);
    g.stroke();
  }
  // The sea: waves round it.
  g.strokeStyle = 'rgba(60,90,110,0.6)';
  g.lineWidth = 1.5;
  for (let i = 0; i < 26; i++) {
    const x = 40 + (i * 97) % 700;
    const y = 30 + ((i * 53) % 4) * 125;
    g.beginPath();
    for (let k = 0; k < 24; k += 2) g.lineTo(x + k, y + Math.sin(k * 0.6) * 3);
    g.stroke();
  }
  // Under the rim, south: the white patch where nothing is drawn, its edge torn.
  g.fillStyle = '#eef2f5';
  g.beginPath();
  g.moveTo(300, 372);
  for (let k = 0; k <= 20; k++) g.lineTo(300 + k * 9, 372 + Math.sin(k * 1.7) * 6 + (k % 3) * 3);
  g.lineTo(480, 450);
  for (let k = 20; k >= 0; k--) g.lineTo(300 + k * 9, 450 + Math.sin(k * 2.3) * 5);
  g.closePath();
  g.fill();
  g.strokeStyle = 'rgba(141,42,34,0.5)';
  g.setLineDash([5, 6]);
  g.stroke();
  g.setLineDash([]);
  // The margin's notes: lines of a small hand.
  g.strokeStyle = 'rgba(40,46,54,0.55)';
  g.lineWidth = 1;
  for (let i = 0; i < 9; i++) {
    g.beginPath();
    g.moveTo(24, 380 + i * 12);
    g.lineTo(24 + 120 + (i % 3) * 30, 380 + i * 12);
    g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
};

interface CairnView {
  mesh: THREE.Mesh;
  rubble: THREE.Mesh;
  x: number;
  y: number;
  fall: number;
}

interface RingFx {
  /** Where (world), from what radius to what (world units), when it starts and how long it lasts (seconds). */
  x: number;
  z: number;
  r0: number;
  r1: number;
  t0: number;
  life: number;
  color: THREE.Color;
  alpha: number;
}

export class AsterLayer {
  readonly group = new THREE.Group();
  /** The ranger's chapter 2 places: drawn by their own layer. */
  private galo: GaloLayer | null = null;
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private texs: THREE.Texture[] = [];
  private glow = new GlowLayer(220);
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private hour: Hour = 'day';
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private sea: THREE.ShaderMaterial | null = null;
  private seaEdge = 1e9;
  private lastState: BattleState | null = null;
  private tickNow = 0;
  private tickAt = 0;
  // The rim.
  private C: CairnLayout | null = null;
  private cairns: CairnView[] = [];
  private ringMesh: THREE.Mesh | null = null;
  private ringMat: THREE.MeshBasicMaterial | null = null;
  private ringKey = '';
  private fillMesh: THREE.Mesh | null = null;
  private threadMesh: THREE.Mesh | null = null;
  private threadKey = '';
  private labels: THREE.Sprite[] = [];
  private digitMats: THREE.SpriteMaterial[] = [];
  private sunMat: THREE.SpriteMaterial | null = null;
  private seaMat2: THREE.SpriteMaterial | null = null;
  private rightFlash = 0;
  // The fog.
  private E: EchoLayout | null = null;
  private fogs: THREE.ShaderMaterial[] = [];
  private rings: RingFx[] = [];
  private ringPool: THREE.InstancedMesh | null = null;
  /** The crows' silhouettes over the fog (act 2). */
  private crowPool: THREE.InstancedMesh | null = null;
  private walls: { x: number; z: number; t0: number }[] = [];
  // The slope.
  private F: FallenLayout | null = null;
  private baseMesh: THREE.Mesh | null = null;
  private baseKey = '';
  private roller: { g: THREE.Group; body: THREE.Group; ball: THREE.Group; curl: number; spin: number; yaw: number; dust: number } | null = null;
  private lane: THREE.Mesh | null = null;
  private rollerId = 0;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private up = new THREE.Vector3(0, 1, 0);
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `aster-${place}`;
    // The ranger's chapter 2 (the flank's cliff): its own layer (engine/galo.ts), handed every call.
    if (GALO_PLACES.has(place)) {
      this.galo = new GaloLayer(ctx, map, place, s);
      this.group.add(this.galo.group);
      return;
    }
    const kit = new Kit();
    if (place === 'furrowRim') this.buildRim(kit, s);
    else if (place === 'furrowFog') this.buildFog(kit, s);
    else if (place === 'looseSlope') this.buildSlope(kit, s);
    else this.buildScene(kit, place);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.04, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'aster-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    const sm = this.track(new THREE.MeshBasicMaterial({ color: '#0d1210', transparent: true, opacity: 0.22, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3 }));
    const shade = this.shade.build(sm, 'aster-shadows');
    if (shade) {
      this.geos.push(shade.geometry);
      shade.renderOrder = 2;
      this.group.add(shade);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
  }

  private rand(i: number): number {
    const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
  }

  /** A cairn: a broad foot stone, four stones narrowing up, its cord tied round the third with its knots hanging. */
  private cairnKit(kit: Kit, p: THREE.Vector3, seed: number, plate: boolean, cord = CORD, knots = 4): void {
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const sph = new THREE.SphereGeometry(1, 6, 5);
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 6);
    let y = p.y;
    if (plate) {
      // The high plate: a slab of the shell standing proud of the rim, lichen on its edge.
      kit.add(box, SLATE[1] as string, place(p.x, y + 0.2, p.z, seed, new THREE.Vector3(1.05 * S, 0.4, 0.95 * S)));
      kit.add(box, SLATE[2] as string, place(p.x, y + 0.42, p.z, seed + 0.05, new THREE.Vector3(1.0 * S, 0.06, 0.9 * S)));
      kit.add(box, MOSS[0] as string, place(p.x + 0.35, y + 0.43, p.z - 0.3, seed, new THREE.Vector3(0.35, 0.03, 0.2)));
      y += 0.44;
    }
    const sizes = [0.62, 0.5, 0.4, 0.31, 0.22];
    for (let i = 0; i < sizes.length; i++) {
      const s = sizes[i] as number;
      const k = this.rand(seed * 7 + i);
      kit.add(cap, STONE[(i + Math.floor(k * 5)) % 5] as string, place(p.x + (k - 0.5) * 0.06, y + s * 0.32, p.z + (k - 0.5) * 0.05, k * 6, new THREE.Vector3(s, s * 0.62, s * 0.92)));
      y += s * 0.55;
    }
    // Lichen on the foot stone.
    kit.add(sph, MOSS[1] as string, place(p.x - 0.18, p.y + (plate ? 0.6 : 0.2), p.z + 0.15, 0, new THREE.Vector3(0.12, 0.04, 0.1)));
    // The cord: a band round the third stone, its tail hanging down the east face, a knot every so often.
    const band = new THREE.TorusGeometry(0.21, 0.025, 4, 12);
    const yb = p.y + (plate ? 0.44 : 0) + 0.62 * 0.55 + 0.5 * 0.55 + 0.12;
    kit.add(band, cord, place(p.x, yb, p.z, 0, 1, Math.PI / 2, 0));
    const tail = 0.5;
    kit.add(cyl, cord, place(p.x + 0.22, yb - tail / 2, p.z + 0.06, 0, new THREE.Vector3(0.018, tail, 0.018), 0, 0.1));
    for (let i = 0; i < knots; i++) kit.add(sph, cord, place(p.x + 0.22 + i * 0.006, yb - 0.08 - i * 0.11, p.z + 0.06, 0, 0.04));
    cap.dispose();
    box.dispose();
    sph.dispose();
    cyl.dispose();
    band.dispose();
  }

  /** Rubble where a cairn came apart: its stones strewn round its foot. */
  private rubbleKit(kit: Kit, p: THREE.Vector3, seed: number): void {
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    for (let i = 0; i < 6; i++) {
      const a = this.rand(seed + i) * Math.PI * 2;
      const d = 0.25 + this.rand(seed * 3 + i) * 0.45;
      const s = 0.18 + this.rand(seed * 5 + i) * 0.22;
      kit.add(cap, STONE[i % 5] as string, place(p.x + Math.cos(a) * d, p.y + s * 0.2, p.z + Math.sin(a) * d, a, new THREE.Vector3(s, s * 0.55, s)));
    }
    cap.dispose();
  }

  /** The long shadows (one translucent mesh, built after the dressing). */
  private shade = new Kit();

  /** A long shadow to the east of a thing (the sun is low in the west): a soft dark smear on the ground. */
  private shadowKit(_kit: Kit, p: THREE.Vector3, len: number, w: number): void {
    const disc = new THREE.CircleGeometry(1, 12).rotateX(-Math.PI / 2);
    this.shade.add(disc, '#000000', place(p.x + len * 0.5, p.y + 0.014, p.z, 0, new THREE.Vector3(len * 0.6, 1, w)));
    this.shade.add(disc, '#000000', place(p.x + len * 0.3, p.y + 0.016, p.z, 0, new THREE.Vector3(len * 0.35, 1, w * 0.75)));
    disc.dispose();
  }

  /** Walls of live stone round open cells: blocks of grey-green stone, moss along their tops. */
  private stoneWalls(kit: Kit, rocks: { q: number; r: number }[], open: Set<string>, lo: number, hi: number): void {
    const prism = new THREE.CylinderGeometry(0.5, 0.58, 1, 6);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const near = (q: number, r: number): boolean => [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]].some(([dq, dr]) => open.has(`${q + (dq as number)},${r + (dr as number)}`));
    for (const h of rocks) {
      if (!near(h.q, h.r)) continue;
      const p = this.hex(h.q, h.r);
      const k = this.rand(h.q * 31 + h.r * 7);
      const k2 = this.rand(h.q * 13 + h.r * 17);
      const tall = lo + (hi - lo) * k;
      kit.add(prism, STONE[Math.floor(k2 * 4.99)] as string, place(p.x, p.y + tall * 0.5, p.z, k * 5, new THREE.Vector3(1.12 + k2 * 0.18, tall, 1.06)));
      kit.add(cap, MOSS[Math.floor(k * 3.99)] as string, place(p.x + (k - 0.5) * 0.25, p.y + tall + 0.03, p.z, k * 7, new THREE.Vector3(1.0, 0.24, 0.92)));
      if (k > 0.55) kit.add(cap, STONE[2] as string, place(p.x + 0.2, p.y + tall * 0.6, p.z + 0.3, k * 3, new THREE.Vector3(0.4, 0.3, 0.35)));
    }
    prism.dispose();
    cap.dispose();
  }

  /** The cliff on the east and the sea far below it. */
  private seaCliff(kit: Kit, edgeX: number, zSpan: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    for (let z = -zSpan; z <= zSpan; z += 0.9) {
      const k = this.rand(z * 3.1 + 7);
      const x = edgeX + k * 0.5;
      const g = this.ctx.ground(x - 0.4, z);
      kit.add(box, STONE[3 + Math.floor(k * 1.99)] as string, place(x + 0.3, g - 4, z, 0, new THREE.Vector3(0.8, 8, 1.0)));
      kit.add(box, STONE[4] as string, place(x + 1.1 + k * 0.6, g - 11, z + 0.2, 0.1, new THREE.Vector3(1.2, 8, 1.1)));
      kit.add(cap, MOSS[Math.floor(k * 3.99)] as string, place(x - 0.1, g + 0.04, z, k * 4, new THREE.Vector3(0.45, 0.16, 0.7)));
    }
    box.dispose();
    cap.dispose();
    const sea = seaMat();
    this.sea = sea;
    this.track(sea);
    (sea.uniforms.edge as THREE.IUniform<number>).value = edgeX;
    const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(260, 260).rotateX(-Math.PI / 2)), sea);
    plane.position.set(edgeX + 128, -16, 0);
    plane.name = 'aster-sea';
    this.group.add(plane);
    this.seaEdge = edgeX;
    // Headlands in the haze.
    for (let i = 0; i < 4; i++) kit.add(cap, STONE[4] as string, place(edgeX + 40 + i * 11, -15, -26 + i * 17, i, new THREE.Vector3(10 + i * 2, 7 + (i % 3) * 3, 8)));
  }

  /* ---------- act 1: the rim ---------- */

  private buildRim(kit: Kit, s: BattleState): void {
    const L = cairnsLayout(s.seed);
    this.C = L;
    const groove = new Set(L.furrows.map((h) => `${h.q},${h.r}`));
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    // The Furrows: each groove cell a dark pit under its lips, moss and loose stones along the edge.
    for (const h of L.furrows) {
      const p = this.hex(h.q, h.r);
      const k = this.rand(h.q * 5 + h.r * 11);
      kit.add(new THREE.CylinderGeometry(0.58, 0.5, 0.06, 6), DARK, place(p.x, p.y + 0.015, p.z, Math.PI / 6, new THREE.Vector3(S * 0.98, 1, S * 0.98)));
      for (const [dq, dr] of [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]] as [number, number][]) {
        if (groove.has(`${h.q + dq},${h.r + dr}`)) continue;
        const e = hexToWorld(dq, dr);
        const lx = p.x + e.x * K * 0.5;
        const lz = p.z + e.y * K * 0.5;
        const yaw = Math.atan2(e.x, e.y);
        kit.add(box, STONE[Math.floor(k * 4.99)] as string, place(lx, p.y + 0.1, lz, yaw, new THREE.Vector3(0.62 * S, 0.22, 0.2)));
        kit.add(cap, MOSS[Math.floor(k * 3.99)] as string, place(lx, p.y + 0.24, lz, yaw + k, new THREE.Vector3(0.5, 0.1, 0.22)));
      }
    }
    // Loose stones and moss on the rim; low boulders casting their long shadows east.
    for (let i = 0; i < 46; i++) {
      const q = Math.round((this.rand(i * 3 + 1) - 0.5) * 18);
      const r = Math.round((this.rand(i * 5 + 2) - 0.5) * 18);
      if (Math.abs(q) + Math.abs(r) + Math.abs(q + r) > 16 || groove.has(`${q},${r}`) || L.cairns.some((c) => c.q === q && c.r === r)) continue;
      const p = this.hex(q, r);
      const k = this.rand(i * 7);
      const ox = (this.rand(i * 11) - 0.5) * 0.6;
      const oz = (this.rand(i * 13) - 0.5) * 0.6;
      if (k > 0.55) {
        const sz = 0.3 + k * 0.35;
        kit.add(cap, STONE[i % 5] as string, place(p.x + ox, p.y + sz * 0.25, p.z + oz, k * 6, new THREE.Vector3(sz, sz * 0.6, sz * 0.85)));
        this.shadowKit(kit, new THREE.Vector3(p.x + ox, p.y, p.z + oz), sz * 3, sz * 0.45);
      } else kit.add(cap, MOSS[i % 4] as string, place(p.x + ox, p.y + 0.02, p.z + oz, k * 6, new THREE.Vector3(0.5 + k, 0.05, 0.4 + k * 0.6)));
    }
    // The cairns: each its own mesh, its rubble hidden by it; long shadows east.
    const mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.04, flatShading: true }));
    L.cairns.forEach((c, i) => {
      const p = this.hex(c.q, c.r);
      const plate = !!L.plates[i];
      const ck = new Kit();
      this.cairnKit(ck, p, i * 3 + 1, plate);
      const mesh = ck.build(mat, `aster-cairn-${i}`) as THREE.Mesh;
      mesh.castShadow = true;
      this.track(mesh.geometry);
      this.group.add(mesh);
      const rk = new Kit();
      this.rubbleKit(rk, p, i * 9 + 4);
      const rubble = rk.build(mat, `aster-rubble-${i}`) as THREE.Mesh;
      this.track(rubble.geometry);
      rubble.visible = false;
      this.group.add(rubble);
      this.shadowKit(kit, p, 2.6, 0.32);
      const w = hexToWorld(c.q, c.r);
      this.cairns.push({ mesh, rubble, x: w.x, y: w.y, fall: 0 });
    });
    // The east: the rim's edge and the sea below; the west: the rim going on, higher, in the haze.
    this.seaCliff(kit, (CAIRN_EDGE + 0.4) * S, 16);
    for (let i = 0; i < 18; i++) {
      const z = -14 + i * 1.7;
      const k = this.rand(i * 19);
      kit.add(cap, STONE[i % 5] as string, place(-(CAIRN_EDGE + 1.5 + k * 3) * S, 0.4 + k, z, k * 4, new THREE.Vector3(1.6 + k * 2, 0.8 + k * 1.6, 1.4)));
    }
    box.dispose();
    cap.dispose();
    this.buildRings();
  }

  /** The rings' mesh, the thread's, the numbers and the sun and the sea (filled as the cords are read). */
  private buildRings(): void {
    this.ringMat = this.track(new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.92, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -6 }));
    const g = this.track(new THREE.BufferGeometry());
    this.ringMesh = new THREE.Mesh(g, this.ringMat);
    this.ringMesh.renderOrder = 6;
    this.ringMesh.frustumCulled = false;
    this.ringMesh.name = 'aster-rings';
    this.group.add(this.ringMesh);
    // The two halves under the rings: the sun's pale, the sea's blue.
    const fm = this.track(new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.24, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -5 }));
    this.fillMesh = new THREE.Mesh(this.track(new THREE.BufferGeometry()), fm);
    this.fillMesh.renderOrder = 5;
    this.fillMesh.frustumCulled = false;
    this.group.add(this.fillMesh);
    const tg = this.track(new THREE.BufferGeometry());
    const tm = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0.95, depthWrite: false }));
    this.threadMesh = new THREE.Mesh(tg, tm);
    this.threadMesh.renderOrder = 7;
    this.threadMesh.frustumCulled = false;
    this.group.add(this.threadMesh);
    for (let d = 1; d <= 6; d++) {
      const t = markTex(String(d), '#ff4f3a');
      this.texs.push(t);
      this.digitMats.push(this.track(new THREE.SpriteMaterial({ map: t, depthTest: false, transparent: true })));
    }
    const sun = markTex('', '#f4f7fa', 'sun');
    const sea = markTex('', '#9fd2e6', 'sea');
    this.texs.push(sun, sea);
    this.sunMat = this.track(new THREE.SpriteMaterial({ map: sun, depthTest: false, transparent: true }));
    this.seaMat2 = this.track(new THREE.SpriteMaterial({ map: sea, depthTest: false, transparent: true }));
    for (let i = 0; i < 28; i++) {
      const sp = new THREE.Sprite(this.digitMats[0]);
      sp.visible = false;
      sp.renderOrder = 9;
      sp.scale.set(0.42, 0.42, 1);
      this.labels.push(sp);
      this.group.add(sp);
    }
  }

  /**
   * Rings round cairns (world points), a hundred paces apart (`n` of them, numbered at their west and east corners),
   * colored each its own; the line between the sun's half and the sea's through the first.
   */
  private drawRings(sets: { x: number; y: number; color: THREE.Color; n: number }[]): void {
    const mesh = this.ringMesh;
    if (!mesh) return;
    const pos: number[] = [];
    const col: number[] = [];
    const quad = (ax: number, az: number, bx: number, bz: number, y: number, w: number, c: THREE.Color, a: number): void => {
      const dx = bx - ax;
      const dz = bz - az;
      const l = Math.hypot(dx, dz) || 1;
      const nx = (-dz / l) * w;
      const nz = (dx / l) * w;
      const pts = [ax + nx, y, az + nz, ax - nx, y, az - nz, bx + nx, y, bz + nz, bx + nx, y, bz + nz, ax - nx, y, az - nz, bx - nx, y, bz - nz];
      pos.push(...pts);
      for (let i = 0; i < 6; i++) col.push(c.r * a, c.g * a, c.b * a);
    };
    let li = 0;
    for (const l of this.labels) l.visible = false;
    const corners = [
      [1000, 0],
      [500, -866],
      [-500, -866],
      [-1000, 0],
      [-500, 866],
      [500, 866],
    ];
    sets.forEach((st, si) => {
      const c0 = this.ctx.at(st.x, st.y, 0);
      for (let k = 1; k <= st.n; k++) {
        const a = k % 2 ? 1 : 0.72;
        for (let i = 0; i < 6; i++) {
          const p = corners[i] as [number, number];
          const q = corners[(i + 1) % 6] as [number, number];
          const ax = st.x + p[0] * k;
          const ay = st.y + p[1] * k;
          const bx = st.x + q[0] * k;
          const by = st.y + q[1] * k;
          // Cut into steps along the ground so it follows the relief.
          const steps = 4 * k;
          for (let j = 0; j < steps; j++) {
            const t0 = j / steps;
            const t1 = (j + 1) / steps;
            const u = this.ctx.at(ax + (bx - ax) * t0, ay + (by - ay) * t0, 0.05 + si * 0.01);
            const v = this.ctx.at(ax + (bx - ax) * t1, ay + (by - ay) * t1, 0.05 + si * 0.01);
            quad(u.x, u.z, v.x, v.z, (u.y + v.y) / 2, 0.045, st.color, a);
          }
        }
        // Its number at the west and east corners.
        for (const side of [-1, 1]) {
          const sp = this.labels[li++];
          if (!sp) continue;
          sp.material = this.digitMats[k - 1] as THREE.SpriteMaterial;
          sp.scale.set(0.42, 0.42, 1);
          sp.position.copy(this.ctx.at(st.x + side * 1000 * k + side * 260, st.y - 120, 0.35));
          sp.visible = true;
        }
      }
      if (si === 0 && this.fillMesh) {
        // The halves: the sun's (west) pale, the sea's (east) blue, as far as the last ring.
        const fpos: number[] = [];
        const fcol: number[] = [];
        const n = st.n * 1000;
        const half = (side: number, c: THREE.Color): void => {
          const pts = [
            [0, -866 * st.n],
            [side * 500 * st.n, -866 * st.n],
            [side * n, 0],
            [side * 500 * st.n, 866 * st.n],
            [0, 866 * st.n],
          ] as [number, number][];
          const o = this.ctx.at(st.x, st.y, 0.03);
          for (let i = 0; i + 1 < pts.length; i++) {
            const a = pts[i] as [number, number];
            const b = pts[i + 1] as [number, number];
            const pa = this.ctx.at(st.x + a[0], st.y + a[1], 0.03);
            const pb = this.ctx.at(st.x + b[0], st.y + b[1], 0.03);
            fpos.push(o.x, o.y, o.z, pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
            for (let k = 0; k < 3; k++) fcol.push(c.r, c.g, c.b);
          }
        };
        half(-1, PALE);
        half(1, SEA);
        const fg = this.fillMesh.geometry;
        fg.setAttribute('position', new THREE.Float32BufferAttribute(fpos, 3));
        fg.setAttribute('color', new THREE.Float32BufferAttribute(fcol, 3));
        fg.computeBoundingSphere();
      }
      if (si === 0) {
        // The line between the sun's half (west) and the sea's (east), dashed, north to south.
        for (let j = -st.n * 3; j < st.n * 3; j += 2) {
          const u = this.ctx.at(st.x, st.y + j * 290, 0.06);
          const v = this.ctx.at(st.x, st.y + (j + 1) * 290, 0.06);
          quad(u.x, u.z, v.x, v.z, (u.y + v.y) / 2, 0.03, PALE, 0.8);
        }
        const sun = this.labels[li++];
        if (sun && this.sunMat) {
          sun.material = this.sunMat;
          sun.position.copy(this.ctx.at(st.x - 2500, st.y + 900, 0.6));
          sun.scale.set(0.7, 0.7, 1);
          sun.visible = true;
        }
        const sea = this.labels[li++];
        if (sea && this.seaMat2) {
          sea.material = this.seaMat2;
          sea.position.copy(this.ctx.at(st.x + 2500, st.y + 900, 0.6));
          sea.scale.set(0.7, 0.7, 1);
          sea.visible = true;
        }
      }
      void c0;
    });
    if (!sets.length && this.fillMesh) this.fillMesh.geometry.deleteAttribute('position');
    const g = mesh.geometry;
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    g.computeBoundingSphere();
  }

  /** The red thread from cairn to cairn (world points), sagging a little between. */
  private drawThread(pts: { x: number; y: number }[]): void {
    const mesh = this.threadMesh;
    if (!mesh) return;
    if (pts.length < 2) {
      mesh.visible = false;
      return;
    }
    const path: THREE.Vector3[] = [];
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i] as { x: number; y: number };
      const b = pts[i + 1] as { x: number; y: number };
      for (let j = 0; j <= 16; j++) {
        const t = j / 16;
        const p = this.ctx.at(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, 0);
        p.y += 0.78 - Math.sin(t * Math.PI) * 0.32;
        path.push(p);
      }
    }
    const curve = new THREE.CatmullRomCurve3(path);
    const g = new THREE.TubeGeometry(curve, path.length * 2, 0.03, 5, false);
    mesh.geometry.dispose();
    mesh.geometry = g;
    this.geos.push(g);
    mesh.visible = true;
  }

  /* ---------- act 2: the fog in the Furrow ---------- */

  private buildFog(kit: Kit, s: BattleState): void {
    const L = echoLayout(s.seed);
    this.E = L;
    const open = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    this.stoneWalls(kit, L.rocks, open, 1.5, 2.1);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    // The floor: wet stone, puddles, moss in the corners; a rope left by someone, an old peg.
    for (const h of L.floor) {
      const p = this.hex(h.q, h.r);
      const k = this.rand(h.q * 41 + h.r * 3);
      if (k > 0.62) kit.add(new THREE.CircleGeometry(1, 9).rotateX(-Math.PI / 2), '#2a3236', place(p.x + (k - 0.8) * 0.6, p.y + 0.012, p.z + (k - 0.7) * 0.5, k * 5, new THREE.Vector3(0.36 + k * 0.2, 1, 0.24)));
      else if (k < 0.25) kit.add(cap, STONE[Math.floor(k * 19.9) % 5] as string, place(p.x + (k - 0.12) * 1.6, p.y + 0.06, p.z - (k - 0.12) * 1.2, k * 9, new THREE.Vector3(0.24, 0.12, 0.2)));
    }
    cap.dispose();
    // Four banks of fog, each its own grain and shade: mist on the floor, one at the knees, one at the chest, and the
    // thick one over the walls' tops (opaque: it hides the Furrow's drawing, docs/RENDIMIENTO.md). Inside the round
    // they open in steps, the low ones tighter: near the hero sharp, toward the edge hazier.
    for (const [y, a, lo, hi, sc, sd] of [
      [0.22, 0.55, '#4a555c', '#7d8a92', 0.55, 1.3],
      [0.8, 0.7, '#56626a', '#8f9ca4', 0.4, 5.1],
      [1.6, 0.75, '#606c74', '#a3afb6', 0.3, 9.7],
      [2.85, 1.0, '#66727a', '#c3ccd1', 0.22, 3.9],
    ] as [number, number, string, string, number, number][]) {
      const m = this.track(fogMat(a, lo, hi, sc, sd));
      m.userData.y = y;
      this.fogs.push(m);
      const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(70, 70).rotateX(-Math.PI / 2)), m);
      plane.position.set(0, y, 0);
      plane.renderOrder = 5;
      plane.frustumCulled = false;
      plane.name = 'aster-fog';
      this.group.add(plane);
    }
    this.buildPool();
  }

  /** The shout's and the echoes' rings: an instanced pool of flat rings. */
  private buildPool(): void {
    // A band with a dark edge each side (vertex colours: the instance's colour times white in the middle, near black at
    // the edges), so a pale ring reads on pale fog and a red one on dark stone.
    const ring = this.track(soundRing());
    const m = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', vertexColors: true, transparent: true, opacity: 0.95, depthWrite: false, depthTest: false }));
    const pool = new THREE.InstancedMesh(ring, m, 24);
    pool.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(24 * 3), 3);
    pool.count = 0;
    pool.renderOrder = 8;
    pool.frustumCulled = false;
    pool.name = 'aster-sound';
    this.ringPool = pool;
    this.group.add(pool);
    // The crows' shapes over the banks (only those out in the fog: in the clear round the bird itself is seen).
    const cm = this.track(new THREE.MeshBasicMaterial({ color: '#12151a', transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide }));
    const crows = new THREE.InstancedMesh(this.track(crowShape()), cm, 24);
    crows.count = 0;
    crows.renderOrder = 7;
    crows.frustumCulled = false;
    crows.name = 'aster-crows';
    this.crowPool = crows;
    this.group.add(crows);
  }

  /* ---------- act 3: the slope of loose plates ---------- */

  private buildSlope(kit: Kit, s: BattleState): void {
    const L = fallenLayout(s.seed);
    this.F = L;
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The slabs standing up out of the slope (the rocks), and the plates lying loose and tilted.
    for (const h of L.rocks) {
      const p = this.hex(h.q, h.r);
      const k = this.rand(h.q * 23 + h.r * 29);
      kit.add(box, SLATE[Math.floor(k * 3.99)] as string, place(p.x, p.y + 0.7, p.z, k * 3, new THREE.Vector3(1.05 * S, 1.4 + k * 0.6, 0.38), 0.25 - k * 0.4, 0.2));
      kit.add(box, SLATE[(Math.floor(k * 3.99) + 1) % 4] as string, place(p.x + 0.2, p.y + 1.45 + k * 0.6, p.z, k * 3, new THREE.Vector3(0.9 * S, 0.12, 0.42), 0.25 - k * 0.4, 0.2));
      this.shadowKit(kit, p, 3, 0.5);
    }
    for (const h of L.plates) {
      const p = this.hex(h.q, h.r);
      const k = this.rand(h.q * 37 + h.r * 41);
      kit.add(box, SLATE[Math.floor(k * 3.99)] as string, place(p.x + (k - 0.5) * 0.4, p.y + 0.08, p.z + (k - 0.5) * 0.3, k * 6, new THREE.Vector3(0.7 + k * 0.4, 0.1, 0.5 + k * 0.3), (k - 0.5) * 0.35, (k - 0.5) * 0.3));
      if (k > 0.5) kit.add(box, SLATE[2] as string, place(p.x - 0.3, p.y + 0.05, p.z + 0.25, k * 4, new THREE.Vector3(0.4, 0.07, 0.3), 0.2, -0.15));
    }
    // A scatter of grit and small plates everywhere (the slope moved).
    for (let i = 0; i < 70; i++) {
      const q = Math.round((this.rand(i * 3 + 9) - 0.5) * 20);
      const r = Math.round((this.rand(i * 5 + 4) - 0.5) * 20);
      if (Math.abs(q) + Math.abs(r) + Math.abs(q + r) > 18) continue;
      const p = this.hex(q, r);
      const k = this.rand(i * 17);
      kit.add(box, SLATE[i % 4] as string, place(p.x + (k - 0.5), p.y + 0.03, p.z + (this.rand(i) - 0.5), k * 6, new THREE.Vector3(0.22 + k * 0.2, 0.05, 0.16), (k - 0.5) * 0.4, 0));
    }
    // The cairns standing (each its own mesh: the Roller stops against them), long shadows east.
    const mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.04, flatShading: true }));
    L.cairns.forEach((c, i) => {
      const p = this.hex(c.q, c.r);
      const ck = new Kit();
      this.cairnKit(ck, p, i * 5 + 2, false, i === L.long ? CORD : '#5a5550', i === L.long ? 9 : 3);
      const mesh = ck.build(mat, `aster-cairn-${i}`) as THREE.Mesh;
      this.track(mesh.geometry);
      this.group.add(mesh);
      this.shadowKit(kit, p, 2.6, 0.32);
      const w = hexToWorld(c.q, c.r);
      this.cairns.push({ mesh, rubble: mesh, x: w.x, y: w.y, fall: 0 });
    });
    // The far slope going up north into the haze, the sea far off east.
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    for (let i = 0; i < 16; i++) {
      const x = -16 + i * 2.2;
      const k = this.rand(i * 23);
      kit.add(cap, SLATE[i % 4] as string, place(x, 1.5 + k * 2, -(FALLEN_EDGE + 2 + k * 3) * S, k * 3, new THREE.Vector3(3 + k * 2, 2 + k * 3, 2)));
    }
    cap.dispose();
    box.dispose();
    this.buildRings();
    const bm = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0, flatShading: true, polygonOffset: true, polygonOffsetFactor: -2 }));
    this.baseMesh = new THREE.Mesh(this.track(new THREE.BufferGeometry()), bm);
    this.baseMesh.name = 'aster-bases';
    this.group.add(this.baseMesh);
    this.buildRoller();
    this.buildPool();
    // The Roller's lane while it curls: a long red band on the ground.
    const lm = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0.3, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -5 }));
    this.lane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(0.5, 0, 0)), lm);
    this.lane.visible = false;
    this.lane.renderOrder = 5;
    this.group.add(this.lane);
  }

  /** The bases of the cairns that fell: pale patches ringed with small stones; a cracked one dark, split. */
  private drawBases(st: number[]): void {
    const L = this.F;
    const mesh = this.baseMesh;
    if (!L || !mesh) return;
    const kit = new Kit();
    const disc = new THREE.CircleGeometry(0.5, 14).rotateX(-Math.PI / 2);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    L.bases.forEach((b, i) => {
      const p = this.hex(b.q, b.r);
      const cracked = st[i] === 1;
      kit.add(disc, cracked ? '#3b4044' : '#d5dce1', place(p.x, p.y + 0.02, p.z, 0, new THREE.Vector3(1.5, 1, 1.5)));
      kit.add(disc, cracked ? '#2a2e32' : '#e8eef2', place(p.x, p.y + 0.025, p.z, 0, new THREE.Vector3(0.9, 1, 0.9)));
      for (let k = 0; k < 9; k++) {
        const a = (k / 9) * Math.PI * 2 + i;
        kit.add(cap, STONE[k % 5] as string, place(p.x + Math.cos(a) * 0.7, p.y + 0.07, p.z + Math.sin(a) * 0.7, a, new THREE.Vector3(0.2, 0.12, 0.18)));
      }
      if (cracked)
        for (let k = 0; k < 3; k++) kit.add(box, '#0c0e10', place(p.x, p.y + 0.03, p.z, k * 1.1 + i, new THREE.Vector3(1.2, 0.01, 0.05)));
    });
    disc.dispose();
    cap.dispose();
    box.dispose();
    const g = (kit.build(mesh.material as THREE.Material, 'tmp') as THREE.Mesh).geometry;
    mesh.geometry.dispose();
    mesh.geometry = g;
    this.geos.push(g);
  }

  /** The Roller: an armadillo the size of a cart, its back of the shell's loose plates; curled, a ball of them. */
  private buildRoller(): void {
    const plate = this.track(new THREE.MeshStandardMaterial({ color: '#6f7479', roughness: 0.6, metalness: 0.25, flatShading: true }));
    const plateD = this.track(new THREE.MeshStandardMaterial({ color: '#4a4f55', roughness: 0.7, metalness: 0.2, flatShading: true }));
    const belly = this.track(new THREE.MeshStandardMaterial({ color: '#2f3236', roughness: 0.9, flatShading: true }));
    const eye = this.track(new THREE.MeshStandardMaterial({ color: '#3a0f0a', emissive: '#ff4f3a', emissiveIntensity: 1.6 }));
    const g = new THREE.Group();
    g.name = 'aster-roller';
    const body = new THREE.Group();
    g.add(body);
    // The back: seven plates overlapping from the tail to the neck, each a shell arc, a dark seam between.
    for (let i = 0; i < 7; i++) {
      const w = 0.62 - Math.abs(i - 3) * 0.05;
      const arc = new THREE.Mesh(this.track(new THREE.CylinderGeometry(w, w, 0.26, 12, 1, true, -Math.PI / 2, Math.PI)), i % 2 ? plate : plateD);
      arc.rotation.set(0, 0, Math.PI / 2);
      arc.rotation.order = 'YXZ';
      arc.rotation.y = Math.PI / 2;
      arc.position.set(0, 0.42, -0.72 + i * 0.24);
      arc.scale.set(1, 1, 1);
      body.add(arc);
    }
    const under = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.55, 10, 6)), belly);
    under.scale.set(1, 0.45, 1.5);
    under.position.y = 0.38;
    body.add(under);
    // The head: a long snout under a plate, two ember eyes; the tail of rings.
    const head = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.2, 0.6, 7)), plateD);
    head.rotation.x = Math.PI / 2;
    head.position.set(0, 0.36, 1.0);
    body.add(head);
    for (const k of [-1, 1]) {
      const e = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.045, 6, 5)), eye);
      e.position.set(k * 0.1, 0.44, 0.92);
      body.add(e);
    }
    const tail = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.12, 0.7, 6)), plate);
    tail.rotation.x = -Math.PI / 2 - 0.3;
    tail.position.set(0, 0.32, -1.1);
    body.add(tail);
    for (let i = 0; i < 4; i++) {
      const leg = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.07, 0.09, 0.32, 6)), belly);
      leg.position.set(i % 2 ? 0.42 : -0.42, 0.16, i < 2 ? 0.45 : -0.45);
      body.add(leg);
    }
    // The ball: the same plates closed round it, flat facets.
    const ball = new THREE.Group();
    const core = new THREE.Mesh(this.track(new THREE.IcosahedronGeometry(0.72, 1)), plate);
    ball.add(core);
    for (let i = 0; i < 6; i++) {
      const band = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.73, 0.05, 4, 20)), plateD);
      band.rotation.set(0, (i / 6) * Math.PI, 0);
      ball.add(band);
    }
    ball.position.y = 0.72;
    ball.visible = false;
    g.add(ball);
    g.scale.setScalar(1.15);
    g.visible = false;
    this.group.add(g);
    this.roller = { g, body, ball, curl: 0, spin: 0, yaw: 0, dust: 0 };
  }

  /** Whether the sim's enemy is the Roller (the layer draws it, the board doesn't). */
  owns(id: number): boolean {
    if (this.galo) return this.galo.owns(id, null);
    return this.place === 'looseSlope' && id > 0 && id === this.rollerId;
  }

  /* ---------- the scenes ---------- */

  private buildScene(kit: Kit, place0: string): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cap = new THREE.DodecahedronGeometry(0.5, 0);
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 12);
    const c = this.hex(0, 0);
    if (place0 === 'asterMap' || place0 === 'asterTable' || place0 === 'asterYard') {
      const inside = place0 !== 'asterYard';
      if (inside) {
        // The Atalaya's room: a ring of coursed stone, beams, a narrow window to the sea; a lamp over the table.
        for (let i = 0; i < 30; i++) {
          const a = (i / 30) * Math.PI * 2;
          const x = c.x + Math.cos(a) * 4.6;
          const z = c.z + Math.sin(a) * 4.6;
          const near = Math.sin(a) > 0.55;
          const t = near ? 0.6 : 3.2;
          kit.add(box, ['#4c5258', '#454b51', '#545a60'][i % 3] as string, place(x, c.y + t / 2, z, -a, new THREE.Vector3(1.05, t, 0.6)));
        }
        kit.add(box, '#2c2622', place(c.x, c.y + 3.25, c.z, 0, new THREE.Vector3(9, 0.18, 0.3)));
        kit.add(box, '#2c2622', place(c.x, c.y + 3.25, c.z, Math.PI / 2, new THREE.Vector3(9, 0.18, 0.3)));
        // The window: the sea at night beyond it.
        kit.add(box, '#16232e', place(c.x + 2.6, c.y + 2.1, c.z - 3.7, -0.6, new THREE.Vector3(0.8, 1.2, 0.1)));
        this.light(new THREE.Vector3(c.x + 2.6, c.y + 2.1, c.z - 3.6), '#7fa6c8', 1.6, 0.35);
        // The map pinned to the north wall (the Atalaya's).
        if (place0 === 'asterTable') this.mapPlane(new THREE.Vector3(c.x - 0.4, c.y + 1.85, c.z - 4.15), 2.6, 0, 0);
      } else {
        // The yard at night: flags, the keep's wall behind, two torches.
        for (let i = -5; i <= 5; i++) kit.add(box, ['#3a4046', '#41474d'][i & 1] as string, place(c.x + i * 1.1, c.y + 1.6, c.z - 4.4, 0, new THREE.Vector3(1.1, 3.2, 0.8)));
        for (const k of [-1, 1]) {
          kit.add(cyl, '#2c3036', place(c.x + k * 3.2, c.y + 0.8, c.z - 3.6, 0, new THREE.Vector3(0.06, 1.6, 0.06)));
          this.light(new THREE.Vector3(c.x + k * 3.2, c.y + 1.7, c.z - 3.6), WARM, 1.4, 0.5, 1);
        }
      }
      // The table (two barrels and a board, in the yard), the map on it, ropes coiled, a lamp, the knotted cords.
      // (North of the people: they stand round it, not on it.)
      const tx = c.x + 0.2;
      const tz = c.z - 1.95;
      if (inside) {
        kit.add(box, '#3a3430', place(tx, c.y + 0.82, tz, 0.05, new THREE.Vector3(2.4, 0.1, 1.4)));
        for (const [dx, dz] of [
          [-1.05, -0.55],
          [1.05, -0.55],
          [-1.05, 0.55],
          [1.05, 0.55],
        ] as [number, number][])
          kit.add(box, '#2e2925', place(tx + dx, c.y + 0.4, tz + dz, 0, new THREE.Vector3(0.12, 0.8, 0.12)));
      } else {
        for (const dx of [-0.75, 0.75]) {
          kit.add(cyl, '#3a3430', place(tx + dx, c.y + 0.42, tz, 0, new THREE.Vector3(0.36, 0.84, 0.36)));
          for (const y of [0.15, 0.7]) kit.add(cyl, '#2c3036', place(tx + dx, c.y + y, tz, 0, new THREE.Vector3(0.38, 0.05, 0.38)));
        }
        kit.add(box, '#3a3430', place(tx, c.y + 0.87, tz, 0.03, new THREE.Vector3(2.2, 0.06, 1.0)));
      }
      this.mapPlane(new THREE.Vector3(tx, c.y + (inside ? 0.88 : 0.91), tz), 1.9, -Math.PI / 2, 0.05);
      for (const [dx, dz] of [
        [1.4, 0.4],
        [-1.4, 0.6],
      ] as [number, number][]) {
        kit.add(new THREE.TorusGeometry(0.16, 0.045, 6, 14), '#5f6a64', place(tx + dx, c.y + 0.04, tz + dz, 0, 1, Math.PI / 2, 0));
        kit.add(new THREE.TorusGeometry(0.11, 0.04, 6, 12), '#56605b', place(tx + dx, c.y + 0.1, tz + dz, 0, 1, Math.PI / 2, 0));
      }
      // Three cords with their knots on the map's edge (Aster's way, as Orla reads it).
      for (let i = 0; i < 3; i++) {
        kit.add(box, CORD, place(tx + 0.7, c.y + 0.92, tz + 0.3 - i * 0.12, 0.2, new THREE.Vector3(0.5, 0.015, 0.015)));
        for (let k = 0; k < 3 + i; k++) kit.add(cap, CORD, place(tx + 0.5 + k * 0.08, c.y + 0.93, tz + 0.3 - i * 0.12, 0, 0.035));
      }
      // The lamp: an iron cage, a cold white light over the map.
      kit.add(box, '#23272c', place(tx - 0.9, c.y + 0.98, tz - 0.4, 0.6, new THREE.Vector3(0.16, 0.22, 0.16)));
      kit.add(box, '#e8eef8', place(tx - 0.9, c.y + 0.99, tz - 0.4, 0.6, new THREE.Vector3(0.11, 0.15, 0.11)));
      this.light(new THREE.Vector3(tx - 0.9, c.y + 1.02, tz - 0.4), COLD, 2.2, 0.85, 1);
      this.hour = inside ? 'inside' : 'night';
    } else if (place0 === 'furrowLip' || place0 === 'fallenBase') {
      // The rim with a Furrow along it (the lip) or the slope's plates (the base), a cairn or a base in the middle.
      const lip = place0 === 'furrowLip';
      for (let q = -6; q <= 6; q++) {
        const p = this.hex(q, -1);
        if (!lip) break;
        kit.add(new THREE.CylinderGeometry(0.58, 0.5, 0.06, 6), DARK, place(p.x, p.y + 0.015, p.z, Math.PI / 6, new THREE.Vector3(S, 1, S)));
        kit.add(box, STONE[(q + 6) % 5] as string, place(p.x, p.y + 0.1, p.z + 0.52, 0, new THREE.Vector3(S, 0.22, 0.2)));
        kit.add(cap, MOSS[(q + 6) % 4] as string, place(p.x + 0.2, p.y + 0.24, p.z + 0.52, q, new THREE.Vector3(0.5, 0.1, 0.22)));
      }
      if (lip) {
        const p = this.hex(0, 0);
        this.cairnKit(kit, p, 5, false);
        // The fresh notch: a pale cut on the foot stone, its chips on the ground.
        kit.add(box, '#e8eef2', place(p.x + 0.05, p.y + 0.2, p.z + 0.36, 0.4, new THREE.Vector3(0.28, 0.03, 0.02), 0, 0.5));
        for (let i = 0; i < 4; i++) kit.add(cap, '#c9d0d6', place(p.x + 0.3 + i * 0.07, p.y + 0.02, p.z + 0.5 + (i % 2) * 0.06, i, 0.05));
        this.shadowKit(kit, p, 2.6, 0.32);
        this.hour = 'fog';
      } else {
        const p = this.hex(0, 0);
        kit.add(new THREE.CircleGeometry(0.5, 14).rotateX(-Math.PI / 2), '#d5dce1', place(p.x, p.y + 0.02, p.z, 0, new THREE.Vector3(1.5, 1, 1.5)));
        for (let k = 0; k < 9; k++) {
          const a = (k / 9) * Math.PI * 2;
          kit.add(cap, STONE[k % 5] as string, place(p.x + Math.cos(a) * 0.7, p.y + 0.07, p.z + Math.sin(a) * 0.7, a, new THREE.Vector3(0.2, 0.12, 0.18)));
        }
        // The red cord coiled on it, new, its two knots: one simple, one tight.
        kit.add(new THREE.TorusGeometry(0.22, 0.03, 5, 16), CORD_NEW, place(p.x, p.y + 0.05, p.z, 0, 1, Math.PI / 2, 0));
        kit.add(new THREE.TorusGeometry(0.14, 0.03, 5, 14), CORD_NEW, place(p.x + 0.05, p.y + 0.09, p.z, 0.4, 1, Math.PI / 2, 0));
        kit.add(cap, CORD_NEW, place(p.x + 0.34, p.y + 0.06, p.z + 0.08, 0, 0.07));
        kit.add(cap, '#a82a20', place(p.x + 0.46, p.y + 0.06, p.z + 0.12, 0, 0.09));
        this.light(new THREE.Vector3(p.x, p.y + 0.3, p.z), RED, 0.9, 0.3);
        for (let i = 0; i < 26; i++) {
          const a = this.rand(i * 3) * Math.PI * 2;
          const d = 1.6 + this.rand(i * 7) * 5;
          kit.add(box, SLATE[i % 4] as string, place(p.x + Math.cos(a) * d, p.y + 0.06, p.z + Math.sin(a) * d, a, new THREE.Vector3(0.6 + this.rand(i) * 0.5, 0.1, 0.45), (this.rand(i * 5) - 0.5) * 0.4, (this.rand(i * 9) - 0.5) * 0.3));
        }
        this.hour = 'dusk';
      }
      for (let i = 0; i < 30; i++) {
        const a = this.rand(i * 13) * Math.PI * 2;
        const d = 2 + this.rand(i * 17) * 5;
        const k = this.rand(i * 19);
        kit.add(cap, k > 0.5 ? (STONE[i % 5] as string) : (MOSS[i % 4] as string), place(c.x + Math.cos(a) * d, c.y + 0.06, c.z + Math.sin(a) * d, a, new THREE.Vector3(0.2 + k * 0.4, 0.1 + k * 0.2, 0.2 + k * 0.3)));
      }
      this.seaCliff(kit, 7.2 * S, 10);
    } else if (place0 === 'furrowCamp') {
      // Under a plate of the shell standing on two rocks: a dry hollow on the rim, at night; the fire is the scene's.
      const p = this.hex(0, -1);
      kit.add(box, SLATE[0] as string, place(p.x, p.y + 1.9, p.z - 0.2, 0.15, new THREE.Vector3(4.2, 0.35, 2.4), 0.12, 0));
      for (const k of [-1, 1]) kit.add(cap, STONE[2] as string, place(p.x + k * 1.8, p.y + 0.9, p.z - 0.4, k, new THREE.Vector3(0.9, 1.9, 1.1)));
      kit.add(box, SLATE[1] as string, place(p.x, p.y + 2.1, p.z - 0.2, 0.15, new THREE.Vector3(4.0, 0.06, 2.2), 0.12, 0));
      for (let i = 0; i < 22; i++) {
        const a = this.rand(i * 3) * Math.PI * 2;
        const d = 2.4 + this.rand(i * 7) * 4;
        kit.add(cap, i % 2 ? (MOSS[i % 4] as string) : (STONE[i % 5] as string), place(c.x + Math.cos(a) * d, c.y + 0.05, c.z + Math.sin(a) * d, a, new THREE.Vector3(0.3, 0.12, 0.25)));
      }
      // The ropes against the rock; an apple half peeled on a stone by Sela.
      kit.add(new THREE.TorusGeometry(0.24, 0.07, 6, 14), '#5f6a64', place(p.x - 1.2, p.y + 0.06, p.z + 0.2, 0, 1, Math.PI / 2, 0));
      kit.add(new THREE.SphereGeometry(0.07, 8, 6), '#7a2a22', place(c.x - 0.7, c.y + 0.2, c.z + 0.9, 0, 1));
      this.hour = 'night';
    }
    box.dispose();
    cap.dispose();
    cyl.dispose();
  }

  /** Aster's map as a sheet: flat on a table (`tilt` −π/2) or pinned upright to a wall. */
  private mapPlane(p: THREE.Vector3, w: number, tilt: number, yaw: number): void {
    const t = mapTex();
    this.texs.push(t);
    const m = this.track(new THREE.MeshStandardMaterial({ map: t, roughness: 0.95, metalness: 0, side: THREE.DoubleSide }));
    const sheet = new THREE.Mesh(this.track(new THREE.PlaneGeometry(w, w * 0.667)), m);
    sheet.rotation.set(tilt, yaw, 0);
    sheet.position.copy(p);
    sheet.name = 'aster-map';
    this.group.add(sheet);
  }

  /* ---------- the board, repainted ---------- */

  /** An enemy's bar width over it (0: none). */
  barOf(id: number): number {
    return this.galo ? this.galo.barOf(id) : 1.4;
  }

  apply(terrain: TerrainBuild): void {
    if (this.galo) return this.galo.apply(terrain);
    this.terrain = terrain;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const rock = new Set<string>();
    for (const h of this.map.rocks ?? []) rock.add(`${h.q},${h.r}`);
    const fog = this.place === 'furrowFog';
    const slope = this.place === 'looseSlope' || this.place === 'fallenBase';
    const room = this.place === 'asterMap' || this.place === 'asterTable';
    const yard = this.place === 'asterYard';
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.3 + v.z * 0.7) * Math.cos(v.z * 1.1 - v.x * 0.4);
        const k2 = 0.5 + 0.5 * Math.sin(v.x * 3.1 - v.z * 2.3);
        if (fog) c.set(['#454e50', '#3d4648', '#4b5456'][Math.floor(k * 2.99)] as string).multiplyScalar(0.95 + lum * 0.05);
        else if (slope) c.set(SLATE[Math.floor(k * 3.99)] as string).multiplyScalar(1.0 + lum * 0.05 + k2 * 0.08);
        else if (room) c.set(['#3c4146', '#363b40', '#42474c'][Math.floor(k * 2.99)] as string).multiplyScalar(0.8 + lum * 0.3);
        else if (yard) c.set(k > 0.6 ? '#343a40' : '#2e3439').multiplyScalar(0.8 + lum * 0.4);
        // The rim: live stone, moss in drifts.
        else c.set(k2 > 0.62 ? (MOSS[Math.floor(k * 3.99)] as string) : (STONE[Math.floor(k * 4.99)] as string)).multiplyScalar(1.0 + lum * 0.05);
        if (fog) {
          const hq = Math.round(v.x / S - v.z / (S * 0.866) / 2);
          const hr = Math.round(v.z / (S * 0.866));
          if (rock.has(`${hq},${hr}`)) c.set('#2a3133');
        }
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      if (o.name === 'outskirts-ground') {
        if (room) o.visible = false;
        else {
          const g = (o as THREE.Mesh).geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
          if (g) {
            for (let i = 0; i < g.count; i++) {
              const lum = g.getX(i) * 0.3 + g.getY(i) * 0.59 + g.getZ(i) * 0.11;
              c.set(fog ? '#3d4648' : slope ? (SLATE[3] as string) : yard ? '#2a3035' : (STONE[3] as string)).multiplyScalar(0.8 + lum * 0.4);
              g.setXYZ(i, c.r, c.g, c.b);
            }
            g.needsUpdate = true;
          }
        }
      }
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    if (this.seaEdge < 1e8) this.carve(terrain);
    if (terrain.fog) terrain.fog.group.visible = !room;
    if (!room) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    const hour: Hour = this.place === 'furrowRim' ? 'day' : fog ? 'fog' : this.place === 'looseSlope' ? 'day' : this.hour;
    this.paint(hour);
  }

  /** The ground east of the cliff's edge cut away (the sea below). */
  private carve(terrain: TerrainBuild): void {
    const edge = this.seaEdge;
    const cut = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!pos) return;
      mesh.updateMatrixWorld(true);
      const mw = mesh.matrixWorld;
      const v = new THREE.Vector3();
      const idx = geo.index;
      const tri = (i: number): number => (idx ? idx.getX(i) : i);
      const n = idx ? idx.count : pos.count;
      const keep: number[] = [];
      for (let i = 0; i < n; i += 3) {
        let cx = 0;
        for (let k = 0; k < 3; k++) cx += v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw).x;
        const out = cx / 3 > edge;
        if (idx) {
          if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
        } else if (out) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const z = pos.getZ(i);
          pos.setXYZ(i + 1, x, y, z);
          pos.setXYZ(i + 2, x, y, z);
        }
      }
      if (idx) geo.setIndex(keep);
      pos.needsUpdate = true;
      geo.computeBoundingSphere();
    };
    cut(terrain.mesh);
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') cut(o as THREE.Mesh);
    });
  }

  paint(hour0: string): void {
    if (this.galo) return this.galo.paint(hour0);
    const hour = (hour0 in HOURS ? hour0 : 'day') as Hour;
    // The rooms keep their own light whatever the scene's sky says.
    const look = HOURS[this.place === 'asterMap' || this.place === 'asterTable' ? 'inside' : this.place === 'furrowFog' ? 'fog' : this.place === 'furrowLip' ? 'fog' : hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    // The sun low in the west (the knots' «hacia el sol»).
    if (this.dome) this.dome.u.uSunDir.value.set(-0.92, hour === 'dusk' ? 0.08 : 0.22, -0.2);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    if (this.galo) return this.galo.tick(s);
    this.tickNow = s.tick;
    this.tickAt = this.now;
    const t = this.now;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      if (e.kind === 'cairnRead' || e.kind === 'cairnRight') {
        const p = this.ctx.at(e.x, e.y, 0.9);
        this.ctx.flare(p, e.kind === 'cairnRight' ? '#ff4f3a' : '#e8eef2', e.kind === 'cairnRight' ? 3.2 : 2, 12);
        this.rings.push({ x: p.x, z: p.z, r0: 0.3, r1: 6 * S, t0: t, life: 1.4, color: e.kind === 'cairnRight' ? RED : PALE, alpha: 0.8 });
        if (e.kind === 'cairnRight') {
          this.rightFlash = 1;
          this.ctx.vfx.burst(p, { n: 20, color: '#ff8a6e', speed: 2, up: 2.2, size: 0.12, life: 14 });
        }
      } else if (e.kind === 'cairnFalse') {
        const v = this.cairns[e.n ?? -1];
        if (v) v.fall = 1;
        const p = this.ctx.at(e.x, e.y, 0.4);
        this.ctx.shake(0.4);
        this.ctx.vfx.burst(p, { n: 26, color: '#8d969c', speed: 3, up: 2.4, size: 0.2, life: 14, smoke: true, gravity: 1 });
        // The noise of it: a ring going out over the rim.
        this.rings.push({ x: p.x, z: p.z, r0: 0.3, r1: 9 * S, t0: t, life: 1.2, color: RED_SOFT, alpha: 0.7 });
      } else if (e.kind === 'shout') {
        this.shout(s, e.x, e.y, e.n ?? -1, t);
      } else if (e.kind === 'echo') {
        // The echo back at Sela: a red ring closing in on her.
        const sela = s.senda?.npcs.find((n) => n.id === 'sela');
        if (sela) {
          const p = this.ctx.at(sela.x, sela.y, 0.3);
          this.rings.push({ x: p.x, z: p.z, r0: 1.4, r1: 0.3, t0: t, life: 0.5, color: RED, alpha: 1 });
        }
      } else if (e.kind === 'deadWall') {
        const p = this.ctx.at(e.x, e.y, 0.8);
        this.ctx.shake(0.15);
        this.ctx.vfx.burst(p, { n: 12, color: '#1a1d20', speed: 2.4, up: 1.6, size: 0.18, life: 14 });
      } else if (e.kind === 'fork') {
        this.ctx.flare(this.ctx.at(e.x, e.y, 0.6), '#e8eef2', 1.8, 10);
      } else if (e.kind === 'rollerIn') {
        this.ctx.shake(0.3);
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.3), { n: 30, color: '#7a8086', speed: 2.6, up: 1.4, size: 0.28, life: 18, smoke: true });
      } else if (e.kind === 'rollWarn') {
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.9), { n: 10, color: '#c9ced6', speed: 1, up: 0.8, size: 0.1, life: 10 });
      } else if (e.kind === 'roll') {
        this.ctx.shake(0.25);
      } else if (e.kind === 'rollHit') {
        this.ctx.shake(0.55);
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.5), { n: 18, color: '#ff8a6e', speed: 3, up: 1.4, size: 0.12, life: 10 });
      } else if (e.kind === 'rollStun') {
        const p = this.ctx.at(e.x, e.y, 0.6);
        this.ctx.shake(0.7);
        this.ctx.vfx.burst(p, { n: 30, color: '#9aa3ad', speed: 3.2, up: 2.2, size: 0.24, life: 16, smoke: true });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.1), 0.4, 2.6, '#e8eef2', 8, 0.9);
      } else if (e.kind === 'baseWrong') {
        const p = this.ctx.at(e.x, e.y, 0.2);
        this.ctx.shake(0.5);
        this.ctx.vfx.burst(p, { n: 24, color: '#6a7076', speed: 2.6, up: 1.8, size: 0.2, life: 14, smoke: true, gravity: 1 });
        this.rings.push({ x: p.x, z: p.z, r0: 0.3, r1: 9 * S, t0: t, life: 1.2, color: RED_SOFT, alpha: 0.7 });
      } else if (e.kind === 'baseRight') {
        const p = this.ctx.at(e.x, e.y, 0.5);
        this.rightFlash = 1;
        this.ctx.flare(p, '#ff4f3a', 4, 16);
        this.ctx.vfx.burst(p, { n: 36, color: '#ff8a6e', speed: 2.4, up: 3, size: 0.14, life: 18 });
        this.rings.push({ x: p.x, z: p.z, r0: 0.3, r1: 8 * S, t0: t, life: 1.6, color: RED, alpha: 0.9 });
      }
    }
  }

  /** The shout: its ring out from Sela, and each dead end's wall in her fork flashing as the ring reaches it. */
  private shout(s: BattleState, x: number, y: number, fork: number, t: number): void {
    const r = s.rules?.senda?.echo;
    const p = this.ctx.at(x, y, 0.3);
    const far = (r?.far ?? 7000) * K;
    const speed = ((r?.speed ?? 250) * 12 * K) / 1;
    this.rings.push({ x: p.x, z: p.z, r0: 0.2, r1: far, t0: t, life: far / speed, color: PALE, alpha: 0.85 });
    const f = r?.forks[fork];
    if (!f) return;
    for (const m of f.mouths) {
      if (m.open) continue;
      const d = Math.hypot(m.end.x - x, m.end.y - y) * K;
      if (d > far) continue;
      const w = this.ctx.at(m.end.x, m.end.y, 0.3);
      // The echo coming back from its wall: red, the same speed, to Sela.
      this.walls.push({ x: w.x, z: w.z, t0: t + d / speed });
      this.rings.push({ x: w.x, z: w.z, r0: 0.2, r1: d, t0: t + d / speed, life: d / speed, color: RED, alpha: 0.95 });
    }
  }

  draw(s: BattleState): void {
    if (this.galo) return this.galo.draw(s);
    this.lastState = s;
    this.tickNow = s.tick;
    const sr = s.rules?.senda;
    // The rim: the rings of the cord being read, the thread of those found, the false ones down.
    const w = s.senda?.cairns;
    if (sr?.cairns && w) {
      const key = `${w.reading}`;
      if (key !== this.ringKey) {
        this.ringKey = key;
        const c = sr.cairns.cairns[w.reading];
        this.drawRings(c ? [{ x: c.x, y: c.y, color: RED, n: 6 }] : []);
      }
      const tk = `${w.step}`;
      if (tk !== this.threadKey) {
        this.threadKey = tk;
        const pts = sr.cairns.way.slice(0, w.step).map((i) => sr.cairns!.cairns[i] as { x: number; y: number });
        this.drawThread(pts);
      }
      this.cairns.forEach((v, i) => {
        if (w.st[i] === 2 && v.fall === 0) v.fall = 1;
      });
    }
    // The slope: the rings of the last two read, the bases, the Roller.
    const f = s.senda?.fallen;
    if (sr?.fallen && f) {
      const key = f.rings.join(',');
      if (key !== this.ringKey) {
        this.ringKey = key;
        const sets: { x: number; y: number; color: THREE.Color; n: number }[] = [];
        for (const i of [f.rings[1], f.rings[0]]) {
          const c = i !== undefined && i >= 0 ? sr.fallen.cairns[i] : undefined;
          if (c) sets.push({ x: c.x, y: c.y, color: sets.length ? PALE : RED, n: 6 });
        }
        this.drawRings(sets);
      }
      const bk = f.bases.join(',');
      if (bk !== this.baseKey) {
        this.baseKey = bk;
        this.drawBases(f.bases);
      }
      if (f.done >= 0 && this.threadKey !== 'done') {
        this.threadKey = 'done';
        const b = sr.fallen.bases[sr.fallen.truth];
        const a = sr.fallen.cairns[sr.fallen.long];
        const n = sr.fallen.cairns[sr.fallen.named];
        if (a && b && n) this.drawThread([a, b, n]);
      }
      this.rollerId = f.roller.id;
    }
  }

  /** The Roller drawn where the sim has it: walking, curling (its lane red ahead), rolling, stunned. */
  private frameRoller(s: BattleState, dt: number): void {
    const R = this.roller;
    const f = s.senda?.fallen;
    const r = s.rules?.senda?.fallen;
    if (!R || !f || !r) return;
    const e = f.roller.id ? s.enemies.find((x) => x.id === f.roller.id) : undefined;
    R.g.visible = !!e && e.hp > 0;
    if (this.lane) this.lane.visible = false;
    if (!e || e.hp <= 0) return;
    const drawn = this.ctx.pos(e.id);
    const p = drawn ? drawn.clone() : this.ctx.at(e.x, e.y, 0);
    p.y = this.ctx.ground(p.x, p.z);
    R.g.position.copy(p);
    const mode = f.roller.mode;
    const curled = mode === 1 || mode === 2 || (mode === 3 && e.stun > 0);
    R.curl += ((curled ? 1 : 0) - R.curl) * Math.min(1, dt * (curled ? 7 : 3));
    R.body.visible = R.curl < 0.6;
    R.ball.visible = R.curl >= 0.35;
    R.body.scale.setScalar(1 - R.curl * 0.4);
    R.ball.scale.setScalar(0.6 + R.curl * 0.4);
    const yaw = facingToRotY(e.facing);
    R.yaw += ((yaw - R.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
    R.yaw = mode === 2 ? yaw : R.yaw * 0.85 + yaw * 0.15;
    R.g.rotation.y = R.yaw;
    if (mode === 2) {
      R.spin += dt * 11;
      R.ball.rotation.x = R.spin;
      if (this.now > R.dust) {
        R.dust = this.now + 0.06;
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.15), { n: 3, color: '#8d959e', speed: 0.8, up: 0.6, size: 0.3, life: 14, smoke: true });
      }
    } else if (e.stun > 0) {
      R.ball.rotation.z = Math.sin(this.now * 18) * 0.12;
      // Stars round it: three pale glints circling.
      for (let i = 0; i < 3; i++) {
        const a = this.now * 4 + (i * Math.PI * 2) / 3;
        this.glow.add(p.x + Math.cos(a) * 0.7, p.y + 1.7, p.z + Math.sin(a) * 0.7, PALE, 0.5, 0.9);
      }
    } else {
      R.body.position.y = Math.abs(Math.sin(this.now * 6)) * 0.04;
    }
    // Curling: the lane it will roll down, red, pulsing faster as it nears; rolling: the lane left.
    if ((mode === 1 || mode === 2) && this.lane) {
      const left = mode === 1 ? Math.max(0, (f.roller.t - s.tick) / Math.max(1, r.roller.warn)) : 0;
      const len = (mode === 1 ? r.roller.len : Math.max(0, f.roller.left)) * K;
      const a = Math.atan2(f.roller.dy, f.roller.dx);
      this.lane.position.set(p.x, p.y + 0.04, p.z);
      this.lane.rotation.y = -a;
      this.lane.scale.set(len, 1, r.roller.r * 2 * K);
      const m = this.lane.material as THREE.MeshBasicMaterial;
      m.opacity = mode === 1 ? 0.2 + 0.25 * (0.5 + 0.5 * Math.sin(this.now * (8 + (1 - left) * 16))) : 0.18;
      this.lane.visible = true;
    }
    this.glow.add(p.x, p.y + 0.6, p.z, RED, mode === 1 ? 1.6 : 0.8, mode === 1 ? 0.4 : 0.15);
  }

  frame(now: number, camera?: THREE.Camera): void {
    if (this.galo) return this.galo.frame(now, camera);
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    const s = this.lastState;
    // The fog opens round the hero.
    if (this.fogs.length && s) {
      const h = s.heroes[0];
      const hp = h ? (this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0)) : null;
      const r = s.rules?.senda?.echo;
      const sight = ((r?.sight ?? 2400) * K) / 1;
      // Sela's own small round (she has to be seen: she is the one who shouts).
      const sela = s.senda?.npcs?.find((n) => n.id === 'sela');
      const sp = sela ? this.ctx.at(sela.x, sela.y, 0) : null;
      for (const m of this.fogs) {
        (m.uniforms.time as THREE.IUniform<number>).value = now;
        if (hp) (m.uniforms.center as THREE.IUniform<THREE.Vector2>).value.set(hp.x, hp.z);
        if (sp) (m.uniforms.center2 as THREE.IUniform<THREE.Vector2>).value.set(sp.x, sp.z);
        // The lower the bank, the nearer it starts: the round goes hazy in steps toward its edge.
        const y = (m.userData.y as number) ?? 2.85;
        const step = y > 2.5 ? 1 : y > 1.2 ? 0.82 : y > 0.5 ? 0.66 : 0.5;
        (m.uniforms.r0 as THREE.IUniform<number>).value = sight * 0.72 * step;
        (m.uniforms.r1 as THREE.IUniform<number>).value = sight * (0.72 * step + 0.46);
        (m.uniforms.r2 as THREE.IUniform<number>).value = sight * 0.36 * step;
      }
    }
    this.glow.begin();
    // The cairns coming apart (a false one): sinking into its rubble.
    for (const v of this.cairns) {
      if (v.fall <= 0 || v.rubble === v.mesh) continue;
      v.fall = Math.min(2, v.fall + dt * 3);
      v.mesh.position.y = -(v.fall - 1) * 1.2;
      v.mesh.rotation.z = (v.fall - 1) * 0.5;
      v.mesh.visible = v.fall < 1.9;
      v.rubble.visible = true;
    }
    // The sound's rings, out and back.
    const pool = this.ringPool;
    let n = 0;
    const live: RingFx[] = [];
    for (const g of this.rings) {
      const k = (now - g.t0) / g.life;
      if (k > 1) continue;
      live.push(g);
      if (k < 0 || !pool || n >= 24) continue;
      const rad = g.r0 + (g.r1 - g.r0) * k;
      const y = this.ctx.ground(g.x, g.z) + 0.25;
      this.m4.compose(this.v3.set(g.x, y, g.z), this.q4.identity(), this.s3.set(rad, 1, rad));
      pool.setMatrixAt(n, this.m4);
      pool.setColorAt(n, this.c3.copy(g.color).multiplyScalar(g.alpha * (1 - k * 0.7)));
      n++;
    }
    this.rings = live;
    if (pool) {
      pool.count = n;
      pool.instanceMatrix.needsUpdate = true;
      if (pool.instanceColor) pool.instanceColor.needsUpdate = true;
    }
    // The dead ends' walls flash red as the shout reaches them.
    this.walls = this.walls.filter((w) => now - w.t0 < 1.2);
    for (const w of this.walls) {
      const k = now - w.t0;
      if (k < 0) continue;
      this.glow.add(w.x, this.ctx.ground(w.x, w.z) + 1.0, w.z, RED, 2.4, 0.9 * (1 - k / 1.2));
    }
    if (s) this.frameRoller(s, dt);
    // The soot crows in the fog: their two ember eyes show through it (drawn over the banks), so a flock is seen coming
    // before the bird is; in the clear round the bird itself reads.
    if (this.fogs.length && s && this.crowPool) {
      const fm = this.fogs[0];
      const c1 = fm?.uniforms.center?.value as THREE.Vector2 | undefined;
      const c2 = fm?.uniforms.center2?.value as THREE.Vector2 | undefined;
      const open = (((s.rules?.senda?.echo?.sight ?? 2400) * K) as number) * 0.62;
      let n = 0;
      for (const e of s.enemies) {
        if (e.kind !== 'crow' || e.hp <= 0) continue;
        const p = this.ctx.pos(e.id) ?? this.ctx.at(e.x, e.y, 0);
        const a = (e.facing / 1024) * Math.PI * 2;
        const fx = Math.cos(a);
        const fz = Math.sin(a);
        const beat = Math.sin(now * 11 + e.id * 1.7);
        const y = p.y + 0.95 + 0.06 * beat;
        // Out in the fog: how far past the clear round's edge (0 in it, 1 well out).
        const d = Math.min(c1 ? Math.hypot(p.x - c1.x, p.z - c1.y) : 99, c2 ? Math.hypot(p.x - c2.x, p.z - c2.y) + 0.4 : 99);
        const out = Math.max(0, Math.min(1, (d - open) / 1.2));
        if (out > 0.05 && n < 24) {
          this.m4.compose(this.v3.set(p.x, 3.05, p.z), this.q4.setFromAxisAngle(this.up, -a), this.s3.set(1.5, 1, 1.5 * (0.55 + 0.45 * Math.abs(beat))));
          this.crowPool.setMatrixAt(n, this.m4);
          n++;
        }
        const ey = out > 0.05 ? 3.1 : y;
        this.glow.add(p.x + fx * 0.25, ey, p.z + fz * 0.25, EMBER_HALO, 2.0, 0.3);
        for (const k of [-1, 1]) this.glow.add(p.x + fx * 0.38 - fz * 0.1 * k, ey + 0.04, p.z + fz * 0.38 + fx * 0.1 * k, EMBER_EYE, 0.45, 1);
      }
      this.crowPool.count = n;
      this.crowPool.instanceMatrix.needsUpdate = true;
    }
    // A cairn found or the base right: the thread and the rings glow a moment.
    this.rightFlash = Math.max(0, this.rightFlash - dt * 0.7);
    if (this.ringMat) this.ringMat.opacity = 0.82 + 0.18 * this.rightFlash;
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    // The cairns' cords glint red; on the rim, Aster's first one glows till read.
    const w = s?.senda?.cairns;
    const cr = s?.rules?.senda?.cairns;
    if (w && cr && w.step === 0) {
      const c = cr.cairns[cr.way[0] as number];
      if (c) {
        const p = this.ctx.at(c.x, c.y, 1.4);
        this.glow.add(p.x, p.y, p.z, RED, 1.4, 0.5 + 0.3 * Math.sin(now * 3));
      }
    }
    if (this.place === 'furrowRim' || this.place === 'furrowLip') {
      // The sea's edge shining (the sea's side).
      for (let z = -12; z <= 12; z += 3) this.glow.add(this.seaEdge + 0.4, -0.2, z, SEA, 2.2, 0.12 + 0.06 * Math.sin(now * 1.3 + z));
    }
    this.glow.end();
    void this.tickAt;
    void this.tickNow;
  }

  dispose(): void {
    if (this.galo) {
      this.galo.dispose();
      this.group.removeFromParent();
      return;
    }
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** The rim's and the slope's edges (hexes): the board's radius. */
const CAIRN_EDGE = 9;
const FALLEN_EDGE = 10;
